import { FC, useContext, useEffect, useMemo } from 'react';

import { TimeInput } from '@smartshell/ui-kit';
import {
  Control,
  Controller,
  useWatch,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form';

import { LocalizationContext } from 'localization';
import { INormalizedTariff } from 'types/tariff';
import { checkIsMultipleDays } from 'utils/tariff';

import s from './SalesTime.module.scss';

interface ITimeIntervalProps {
  control: Control<INormalizedTariff>;
  setValue: UseFormSetValue<INormalizedTariff>;
  trigger: UseFormTrigger<INormalizedTariff>;
  isFormDirty: boolean;
}

const timeToDate = (time: string): Date => {
  const timeDate = new Date();
  const timeItems = time.split(':');

  timeDate.setHours(Number(timeItems[0]));
  timeDate.setMinutes(Number(timeItems[1]));

  return timeDate;
};

export const SalesTime: FC<ITimeIntervalProps> = ({
  control,
  setValue,
  trigger,
  isFormDirty,
}) => {
  const { t } = useContext(LocalizationContext);
  const workingScheduleStart = useWatch({ control, name: 'hours.start' });
  const workingScheduleEnd = useWatch({ control, name: 'hours.end' });

  const isMultipleDays = useMemo(
    () => checkIsMultipleDays(workingScheduleStart, workingScheduleEnd),
    [workingScheduleStart, workingScheduleEnd]
  );

  useEffect(() => {
    if (workingScheduleStart.length === 5) {
      setValue('sales_hours.start', workingScheduleStart);
    }
  }, [workingScheduleStart]);

  useEffect(() => {
    if (!isFormDirty) return;

    if (workingScheduleEnd.length === 5) {
      setValue('sales_hours.end', workingScheduleEnd);
      trigger('sales_hours.end');
    }
  }, [workingScheduleEnd]);

  return (
    <div className={s.container}>
      <div className={s['fields-container']}>
        <Controller
          control={control}
          //TODO: fix ref issue
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <TimeInput
              {...field}
              error={error?.message}
              className={s.time}
              disabled
              bordered
            />
          )}
          name="sales_hours.start"
        />
        <div className={s.separator} />
        <Controller
          control={control}
          //TODO: fix ref issue
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <TimeInput
              {...field}
              error={error?.message}
              className={s.time}
              bordered
            />
          )}
          name="sales_hours.end"
          rules={{
            validate: (value) => {
              if (!value) {
                if (!(workingScheduleStart || workingScheduleEnd)) {
                  return undefined;
                }

                return t('errors.required');
              }

              if (value.length < 5) {
                return t('errors.time.incorrect');
              }

              const start = timeToDate(workingScheduleStart ?? '00:00');
              const end = timeToDate(workingScheduleEnd ?? '00:00');
              const salesEnd = timeToDate(value);

              if (isMultipleDays) {
                if (
                  end.getTime() === start.getTime() &&
                  end.getTime() === salesEnd.getTime()
                ) {
                  return undefined;
                }

                if (salesEnd.getTime() === start.getTime()) {
                  return t('errors.min', workingScheduleStart);
                }

                if (salesEnd < start && salesEnd > end) {
                  return t('tariff.salesTime.out');
                }
              } else {
                if (salesEnd > end) {
                  return t('errors.max', workingScheduleEnd);
                }

                if (
                  salesEnd.getTime() === start.getTime() ||
                  salesEnd < start
                ) {
                  return t('errors.min', workingScheduleStart);
                }
              }
            },
          }}
        />
      </div>
    </div>
  );
};
